import { clientLogger } from '@/lib/clientLogger';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { apiService } from '@/lib/api';
import { ExtendedFieldTypeDTO } from '@/types';
import { useModalLayer } from '@/lib/layerManager';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import { CustomSelect } from './ui/CustomSelect';
import { LoadingSpinner } from './ui/LoadingSpinner';

// Same field types and save call as Settings → Management → Add Field
const FIELD_TYPE_MAPPING: Record<string, ExtendedFieldTypeDTO> = {
  'Text': 'Text',
  'Yes/No': 'Boolean',
};

const EMPTY_FIELD = { name: '', type: 'Text', default_value: '' };

interface AddCustomFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  tableName: string;
  companyId: string;
  onAdded?: () => void;
}

export function AddCustomFieldModal({ isOpen, onClose, tableName, companyId, onAdded }: AddCustomFieldModalProps) {
  const layer = useModalLayer({ enabled: isOpen, onClose });
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const [newField, setNewField] = useState(EMPTY_FIELD);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) setNewField(EMPTY_FIELD);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAdd = async () => {
    if (!newField.name.trim()) {
      addNotification(notificationService.error('Please enter a field name'));
      return;
    }
    try {
      setSaving(true);
      await apiService.addCompanyExtraFieldConfig({
        ...newField,
        name: newField.name.trim(),
        type: FIELD_TYPE_MAPPING[newField.type],
        table_name: tableName,
        default_value: newField.default_value || null,
      }, companyId);
      await queryClient.invalidateQueries({ queryKey: ['extraFieldConfigs', tableName, companyId] });
      addNotification(notificationService.success('Field added successfully'));
      onAdded?.();
      layer.onClose();
    } catch (error) {
      clientLogger.error('Failed to add field:', error);
      addNotification(notificationService.error('Failed to add field'));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4" style={layer.style}>
      <div className="relative flex w-full max-w-lg flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl max-h-[90vh]">
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Add Custom Field</h2>
          <button
            type="button"
            onClick={layer.onClose}
            className="-mr-1 shrink-0 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Field Name *</label>
            <input
              type="text"
              autoFocus
              value={newField.name}
              onChange={(e) => setNewField({ ...newField, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
              className="input-field text-sm"
              placeholder="Enter field name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Field Type *</label>
            <CustomSelect
              value={newField.type}
              onChange={(value) => setNewField({ ...newField, type: value })}
              options={[
                { value: 'Text', label: 'Text' },
                { value: 'Yes/No', label: 'Yes/No' },
              ]}
              placeholder="Select field type"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Default Value (Optional)</label>
            <input
              type="text"
              value={newField.default_value}
              onChange={(e) => setNewField({ ...newField, default_value: e.target.value })}
              className="input-field text-sm"
              placeholder="Enter default value"
            />
          </div>

          <p className="text-xs text-gray-500">The field is added to every job in this company, the same as adding it in Settings.</p>
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t border-gray-100 px-6 py-4">
          <button type="button" onClick={layer.onClose} className="btn-secondary" disabled={saving}>
            Cancel
          </button>
          <button type="button" onClick={handleAdd} className="btn-primary flex items-center gap-2" disabled={saving}>
            {saving && <LoadingSpinner />}
            Add Field
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
